<template>
  <section class="nx-services nx-grid-bg" id="services">
    <div class="nx-services-container">
      <div class="nx-services-header nx-animate-on-scroll">
        <p class="nx-services-subtitle">{{ config?.services?.subtitle }}</p>
        <h2 class="nx-services-title">{{ config?.services?.title }}</h2>
      </div>

      <div class="nx-services-grid">
        <div
          v-for="(item, index) in (config?.services?.items || [])"
          :key="index"
          class="nx-service-card nx-card nx-animate-on-scroll"
          :class="getServiceColorClass(index)"
          :style="{ ...getServiceColorStyle(index), animationDelay: (index * 0.15) + 's' }"
        >
          <div class="nx-service-content-wrapper">
            <div class="nx-service-number">{{ String(index + 1).padStart(2, '0') }}</div>
            <h3 class="nx-service-title">{{ item.title }}</h3>
            <p class="nx-service-content">{{ item.content }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'NxServices',
  props: {
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {};
  },
  mounted() {
    // 组件已挂载，配置由父组件传递
  },
  methods: {
    getServiceColorClass(index) {
      return `nx-service-color-${index}`;
    },
    getServiceColorStyle(index) {
      const colorSets = [
        {
          primary: '#1565c0',
          secondary: '#1976d2',
          accent: '#0288d1',
          glow: 'rgba(21, 101, 192, 0.12)',
          border: 'rgba(21, 101, 192, 0.2)'
        },
        {
          primary: '#00695c',
          secondary: '#00796b',
          accent: '#00897b',
          glow: 'rgba(0, 105, 92, 0.12)',
          border: 'rgba(0, 105, 92, 0.2)'
        },
        {
          primary: '#4527a0',
          secondary: '#512da8',
          accent: '#5e35b1',
          glow: 'rgba(69, 39, 160, 0.12)',
          border: 'rgba(69, 39, 160, 0.2)'
        }
      ];
      const colors = colorSets[index % colorSets.length];
      return {
        '--service-primary': colors.primary,
        '--service-secondary': colors.secondary,
        '--service-accent': colors.accent,
        '--service-glow': colors.glow,
        '--service-border': colors.border
      };
    }
  }
};
</script>

<style scoped>
.nx-services {
  position: relative;
  padding: 80px 40px;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
  overflow: hidden;
}

.nx-services::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 500px;
  background:
    radial-gradient(ellipse at top, rgba(59, 130, 246, 0.05) 0%, transparent 70%),
    radial-gradient(ellipse at 80% 20%, rgba(139, 92, 246, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.nx-services-container {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.nx-services-header {
  text-align: center;
  margin-bottom: 35px;
}

.nx-services-subtitle {
  font-size: 1.2rem;
  color: var(--nx-blue-primary);
  font-weight: 500;
  letter-spacing: 0.3rem;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  text-shadow: 0 0 20px rgba(25, 118, 210, 0.25);
}

.nx-services-title {
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 800;
  color: var(--nx-text-title);
  margin-bottom: 12px;
  text-shadow: 0 0 30px rgba(13, 71, 161, 0.15),
    0 0 60px rgba(59, 130, 246, 0.1);
  position: relative;
  display: inline-block;
}

.nx-services-title::after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--nx-blue-primary),
    transparent
  );
  border-radius: 2px;
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
  animation: titleGlow 2s ease-in-out infinite;
}

.nx-services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.nx-service-card {
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 250, 255, 0.95) 100%);
  backdrop-filter: blur(30px);
  border: 2px solid var(--service-border, rgba(59, 130, 246, 0.15));
  border-radius: 18px;
  padding: 40px 32px;
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow:
    0 8px 30px var(--service-glow, rgba(21, 101, 192, 0.1)),
    0 0 40px var(--service-glow, rgba(21, 101, 192, 0.05));
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg,
      var(--service-primary) 0%,
      var(--service-secondary) 50%,
      var(--service-accent) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 18px 18px 0 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(ellipse at top, var(--service-glow) 0%, transparent 70%),
      radial-gradient(ellipse at bottom, var(--service-glow) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    border-radius: 18px;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: var(--service-border);
    box-shadow:
      0 16px 50px var(--service-glow),
      0 0 70px var(--service-glow);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }

    .nx-service-title {
      color: var(--service-primary);
      text-shadow: 0 0 20px var(--service-glow);
    }

    .nx-service-content {
      color: var(--nx-text-content);
    }
  }
}

.nx-service-content-wrapper {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.nx-service-number {
  position: absolute;
  top: -60px;
  right: 0;
  font-size: 72px;
  font-weight: 900;
  color: var(--service-primary);
  opacity: 0.08;
  line-height: 1;
  pointer-events: none;
  transition: all 0.4s ease;
}

.nx-service-card:hover .nx-service-number {
  opacity: 0.15;
  transform: scale(1.05);
}

.nx-service-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--nx-text-title);
  margin-bottom: 18px;
  transition: all 0.3s ease;
  line-height: 1.4;
  letter-spacing: 0.5px;
}

.nx-service-content {
  font-size: 15px;
  line-height: 1.85;
  color: var(--nx-text-desc);
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  text-indent: 2em;
}

@media (max-width: 1024px) {
  .nx-services {
    padding: 100px 30px;
  }

  .nx-services-grid {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .nx-service-content {
    -webkit-line-clamp: 6;
  }
}

@media (max-width: 768px) {
  .nx-services {
    padding: 80px 20px;
  }

  .nx-services-header {
    margin-bottom: 50px;
  }

  .nx-services-grid {
    gap: 22px;
  }

  .nx-service-card {
    padding: 32px 26px;
  }

  .nx-service-title {
    font-size: 20px;
  }

  .nx-service-content {
    font-size: 14px;
  }
}

/* 标题光效动画 */
@keyframes titleGlow {
  0%, 100% {
    width: 60px;
    opacity: 0.6;
  }
  50% {
    width: 120px;
    opacity: 1;
  }
}
</style>
